import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Bot,
  MapPin,
  ChevronRight,
  Phone,
  Mail,
  Clock,
  Navigation as NavIcon,
  Camera,
  Plus,
  FileText,
  StickyNote,
  Loader2,
  Wrench,
  Star,
  Truck,
  Play,
  Flag,
  Image as ImageIcon,
  X,
  ClipboardCheck,
  CheckCircle2,
  Briefcase,
  ChevronDown,
  ListChecks,
  Circle,
  Calendar,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { jobsApi, customersApi, estimatesApi, invoicesApi, flowsApi, tasksApi, fileUrl, pushApi, techniciansApi } from "../lib/api";
import { getCurrentPositionOnce } from "../lib/gps";
import { PhotoCaptureButton } from "../components/PhotoCaptureButton";
import { DocDetail } from "./Estimates";
import WorkOrderComplete from "../components/mobile/WorkOrderComplete";
import OpenJobAlertModal from "../components/OpenJobAlertModal";
import CommandThemeScope from "../components/CommandThemeScope";
import AssignTechControl from "../components/AssignTechControl";
import { useLocationShare } from "../contexts/LocationShareContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

/* ---------------- helpers ---------------- */
const money0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtDateTime = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "TBD"
    : d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};
const fmtLogTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const STATUS_PILL = {
  Unassigned: "bg-slate-100 text-slate-600",
  Scheduled: "bg-blue-50 text-blue-700",
  Dispatched: "bg-amber-50 text-amber-700",
  "En Route": "bg-amber-50 text-amber-700",
  "On Site": "bg-indigo-50 text-indigo-700",
  "In Progress": "bg-cyan-50 text-cyan-700",
  Completed: "bg-emerald-50 text-emerald-700",
  Canceled: "bg-rose-50 text-rose-700",
};

const STATUS_SOLID = {
  Unassigned: "bg-slate-600 text-white",
  Scheduled: "bg-[#2563EB] text-white",
  Dispatched: "bg-[#D97706] text-white",
  "En Route": "bg-[#D97706] text-white",
  "On Site": "bg-[#4F46E5] text-white",
  "In Progress": "bg-[#0EA5E9] text-white",
  Completed: "bg-[#059669] text-white",
  Canceled: "bg-[#E11D48] text-white",
};

const MEMBERSHIP_STYLE = {
  Platinum: { bg: "bg-violet-100", text: "text-violet-700", dot: "#7C3AED" },
  Gold: { bg: "bg-amber-100", text: "text-amber-700", dot: "#D97706" },
  Silver: { bg: "bg-slate-200", text: "text-slate-600", dot: "#64748B" },
};

// Status transitions a tech can apply, in order.
const STATUS_ACTIONS = [
  { status: "En Route", label: "En Route", icon: Truck },
  { status: "On Site", label: "On Site", icon: MapPin },
  { status: "In Progress", label: "Start Work", icon: Play },
  { status: "Completed", label: "Complete", icon: Flag },
];

const TABS = [
  { key: "job", label: "Job", testid: "mjd-tab-job" },
  { key: "history", label: "History", testid: "mjd-tab-history" },
  { key: "notes", label: "Notes", testid: "mjd-tab-notes" },
  { key: "estimates", label: "Estimates", testid: "mjd-tab-estimates" },
  { key: "invoice", label: "Invoice", testid: "mjd-tab-invoice" },
  { key: "tasks", label: "Tasks", testid: "mjd-tab-tasks" },
  { key: "photos", label: "Photos", testid: "mjd-tab-photos" },
];

/* ---------------- page ---------------- */
export default function MobileJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { requireLocation } = useLocationShare();
  const { hasModule, user } = useAuth();
  const { goBack: historyGoBack } = useNavigationHistory();
  const canAssign = user?.role === "admin" || user?.role === "dispatcher";

  // If we were opened from a specific page (e.g. the mobile Dispatch Board's
  // Unassigned Jobs list), the back button must return there deterministically
  // instead of relying on plain browser history (which can be unreliable after
  // redirects elsewhere in this page, like the completion wizard).
  const backTo = location.state?.from || null;
  const goBack = () => (backTo ? navigate(backTo) : historyGoBack("/mobile"));

  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("job");
  const [showContact, setShowContact] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openEstimate, setOpenEstimate] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(null);
  const [creatingEstimate, setCreatingEstimate] = useState(false);
  const [requiredFlows, setRequiredFlows] = useState(null); // {flows, allComplete, guruSummary}
  const [showCompletion, setShowCompletion] = useState(false);
  const [payrollRecord, setPayrollRecord] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [taskBusy, setTaskBusy] = useState(false);
  const [busyMyPart, setBusyMyPart] = useState(false);
  const [techs, setTechs] = useState([]);
  const [openJobAlert, setOpenJobAlert] = useState(null); // { jobId }
  const [summaryDraft, setSummaryDraft] = useState(job?.guruSummary || "");
  const [savingSummary, setSavingSummary] = useState(false);
  useEffect(() => { setSummaryDraft(job?.guruSummary || ""); }, [job?.id, job?.guruSummary]);
  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      const updated = await jobsApi.updateSummary(id, summaryDraft);
      setJob(updated);
      toast.success("Job Summary updated");
    } catch {
      toast.error("Failed to save Job Summary");
    } finally {
      setSavingSummary(false);
    }
  };

  // "Currently active on this exact screen" signal for push suppression.
  useEffect(() => {
    if (!id) return;
    const ping = () => pushApi.setActiveContext("job", id).catch(() => {});
    ping();
    const timer = setInterval(ping, 30000);
    return () => { clearInterval(timer); pushApi.clearActiveContext().catch(() => {}); };
  }, [id]);

  const load = useCallback(async () => {
    const j = await jobsApi.get(id);
    setJob(j);
    flowsApi.forJob(id).then(setRequiredFlows).catch(() => setRequiredFlows(null));
    tasksApi.list({ jobId: id }).then(setTasks).catch(() => setTasks([]));
    if (canAssign) {
      techniciansApi.list().then(setTechs).catch(() => setTechs([]));
    }
    const tasks = [
      estimatesApi.list().then((all) => all.filter((e) => e.jobId === id)).catch(() => []),
      invoicesApi.list().then((all) => all.filter((i) => i.jobId === id)).catch(() => []),
    ];
    if (j.customerId) {
      tasks.push(customersApi.get(j.customerId).catch(() => null));
    } else {
      tasks.push(Promise.resolve(null));
    }
    const [ests, invs, cust] = await Promise.all(tasks);
    setEstimates(ests);
    setInvoices(invs);
    setCustomer(cust);
  }, [id, canAssign]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch(() => setError("Could not load this job. It may not be assigned to you."))
      .finally(() => setLoading(false));
  }, [load]);

  // Pay recorded for this job (audit trail written once the job is Completed).
  useEffect(() => {
    if (job?.status !== "Completed") { setPayrollRecord(null); return; }
    let cancelled = false;
    const fetchRecord = () => jobsApi.payrollRecord(id).then((r) => { if (!cancelled && r?.id) setPayrollRecord(r); }).catch(() => {});
    fetchRecord();
    const retry = setTimeout(fetchRecord, 2500); // background task may lag slightly behind the status change
    return () => { cancelled = true; clearTimeout(retry); };
  }, [id, job?.status]);

  const sync = async () => {
    setSyncing(true);
    try {
      await load();
      toast.success("Up to date");
    } catch {
      toast.error("Sync failed");
    } finally {
      setTimeout(() => setSyncing(false), 500);
    }
  };

  const membership = useMemo(() => {
    const m = customer?.membership;
    if (m) return m;
    // Fallback for legacy data: derive from tags so existing customers still show a badge.
    const tags = customer?.tags || [];
    if (tags.includes("VIP")) return "Platinum";
    if (tags.includes("Membership")) return "Gold";
    return "";
  }, [customer]);

  const notes = useMemo(
    () => (job?.log || []).filter((l) => l.type === "note").sort((a, b) => (b.time || "").localeCompare(a.time || "")),
    [job],
  );

  const customerNotes = useMemo(
    () =>
      [...((customer && customer.notes) || [])].sort((a, b) =>
        (b.createdAt || "").localeCompare(a.createdAt || ""),
      ),
    [customer],
  );

  const setStatus = async (status, extra = {}) => {
    setBusyStatus(true);
    try {
      // Attach the tech's current GPS for the location-log audit trail on
      // the three field events: On Site, Start Work (In Progress), Complete.
      const gpsEvents = new Set(["En Route", "On Site", "In Progress", "Completed"]);
      const gps = gpsEvents.has(status) ? await getCurrentPositionOnce() : null;
      const updated = await jobsApi.updateStatus(id, status, gps, extra);
      setJob(updated);
      toast.success(`Marked ${status}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "open_job") {
        setOpenJobAlert({ jobId: detail.jobId });
      } else if (err?.response?.status === 409) {
        toast.error((typeof detail === "string" ? detail : null) || "Complete the required checklist first");
        setShowCompletion(true);
      } else {
        toast.error("Could not update status");
      }
    } finally {
      setBusyStatus(false);
    }
  };

  // Marks the job Completed after the completion wizard finishes (backend allows
  // it once every checklist is recorded). Reused by the wizard's onCompleted.
  const completeJob = async (dispositionInfo) => {
    const gps = await getCurrentPositionOnce();
    const targetStatus = dispositionInfo?.status || "Completed";
    const updated = await jobsApi.updateStatus(id, targetStatus, gps, { disposition: dispositionInfo?.name });
    setJob(updated);
    flowsApi.forJob(id).then(setRequiredFlows).catch(() => {});
    load().catch(() => {});
    toast.success("Job completed");
    setShowCompletion(false);
  };

  // "Complete my part" — tech on a multi-tech job releases themselves. If they
  // are the LAST active assignee the mobile app then walks them through the
  // full close-out wizard (signature, parts, payment) which flips the whole
  // job to Completed. Otherwise they get bounced back to the job list to
  // accept the next job while the crew keeps working.
  const handleCompleteClick = async () => {
    const crew = (job?.assignments || []).length;
    if (crew <= 1) {
      // Solo job — legacy behavior.
      setShowCompletion(true);
      return;
    }
    setBusyMyPart(true);
    try {
      const res = await jobsApi.completeMyPart(id);
      setJob(res.job);
      if (res.isLast) {
        toast.success("You're the last one on site — let's wrap it up");
        setShowCompletion(true);
      } else {
        toast.success(
          `Your part is done — ${res.remaining} teammate${res.remaining === 1 ? "" : "s"} still working. You're free to grab the next job.`,
        );
        // Bounce back to the tech dashboard so the next job is one tap away.
        navigate("/mobile");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update your assignment");
    } finally {
      setBusyMyPart(false);
    }
  };

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    setSavingNote(true);
    try {
      const updated = await jobsApi.addNote(id, t);
      setJob(updated);
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  const openTaskCount = useMemo(() => tasks.filter((t) => t.status !== "done").length, [tasks]);

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    setTaskBusy(true);
    try {
      const created = await tasksApi.create({
        title,
        jobId: id,
        customerId: job?.customerId || undefined,
        dueAt: newTaskDue ? new Date(newTaskDue).toISOString() : undefined,
      });
      setTasks((prev) => [created, ...prev]);
      setNewTaskTitle("");
      setNewTaskDue("");
      toast.success("Task added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add task");
    } finally {
      setTaskBusy(false);
    }
  };

  const toggleTask = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    try {
      await tasksApi.update(t.id, { status: next });
    } catch {
      setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: t.status } : x)));
      toast.error("Could not update task");
    }
  };

  const onPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(true);
    try {
      const payload = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (ev) => resolve({ name: file.name, url: ev.target.result, caption: "" });
              reader.readAsDataURL(file);
            }),
        ),
      );
      const updated = await jobsApi.uploadPhotos(id, payload);
      setJob(updated);
      toast.success(`${payload.length} photo${payload.length === 1 ? "" : "s"} added`);
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Customer-file photos (object storage) — added from the field, tagged to this job.
  const [custPhotoBusy, setCustPhotoBusy] = useState(false);
  const [custLightbox, setCustLightbox] = useState(null);
  const customerPhotos = customer?.photos || [];

  const uploadCustomerPhotos = async (files) => {
    if (!job?.customerId) {
      toast.error("This job has no linked customer");
      return;
    }
    if (!files || !files.length || custPhotoBusy) return;
    const count = files.length;
    setCustPhotoBusy(true);
    try {
      const updated = await customersApi.uploadPhotos(job.customerId, files, { source: `job:${job.id}` });
      setCustomer(updated);
      toast.success(`${count} photo${count === 1 ? "" : "s"} added to customer`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setCustPhotoBusy(false);
    }
  };

  // Create a draft estimate then open the full editor (Pick from Pricebook, signature, deposits, email…).
  const createEstimate = async () => {
    if (!job?.customerId) {
      toast.error("This job has no linked customer");
      return;
    }
    setCreatingEstimate(true);
    try {
      const est = await estimatesApi.create({
        customerId: job.customerId,
        jobId: job.id,
        title: `${job.type} — ${job.customer}`,
        lineItems: [],
        taxRate: 0,
        discount: 0,
      });
      setEstimates((prev) => [est, ...prev]);
      setOpenEstimate(est);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create estimate");
    } finally {
      setCreatingEstimate(false);
    }
  };

  const openEstimateDoc = async (id) => {
    try {
      const full = await estimatesApi.get(id);
      setOpenEstimate(full);
    } catch {
      toast.error("Could not open estimate");
    }
  };

  const openInvoiceDoc = async (id) => {
    try {
      const full = await invoicesApi.get(id);
      setOpenInvoice(full);
    } catch {
      toast.error("Could not open invoice");
    }
  };

  if (loading) {
    return (
      <MobileShell>
        <div className="flex flex-col items-center justify-center py-24 text-slate-400">
          <div className="h-8 w-8 border-2 border-slate-300 border-t-[#06B6D4] rounded-full animate-spin mb-3" />
          <div className="text-[13px]">Loading job…</div>
        </div>
      </MobileShell>
    );
  }

  if (error || !job) {
    return (
      <MobileShell>
        <div className="flex flex-col items-center justify-center px-6 py-24 text-center">
          <Wrench className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.75} />
          <div className="text-base font-bold text-slate-700">Job unavailable</div>
          <div className="text-sm text-slate-500 mt-1 max-w-[260px]">{error || "This job could not be found."}</div>
          <button
            onClick={() => historyGoBack("/mobile")}
            data-testid="mjd-back-empty"
            className="mt-5 h-10 px-5 rounded-xl bg-[#0B1E3F] text-white text-sm font-bold"
          >
            Back to dashboard
          </button>
        </div>
      </MobileShell>
    );
  }

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${job.address}, ${job.city}, ${job.state} ${job.zip}`,
  )}`;

  const renderCustomerPhotosCard = () =>
    job.customerId ? (
      <Card>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Customer Photos {customerPhotos.length ? `(${customerPhotos.length})` : ""}</SectionLabel>
          <PhotoCaptureButton
            onFiles={uploadCustomerPhotos}
            busy={custPhotoBusy}
            label="Add"
            testid="mjd-customer-photo"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-[12px] font-bold text-[#0E7490] disabled:opacity-60"
          />
        </div>
        {customerPhotos.length ? (
          <div className="grid grid-cols-3 gap-2" data-testid="mjd-customer-photos-grid">
            {customerPhotos.map((p) => (
              <button
                key={p.id}
                onClick={() => setCustLightbox(p)}
                data-testid={`mjd-customer-photo-${p.id}`}
                className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
              >
                <img src={fileUrl(p.url)} alt={p.caption || "customer photo"} loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        ) : (
          <div className="text-[13px] text-slate-400">
            No customer photos yet. Snap one to save it to {customer?.name || "the customer"}'s file.
          </div>
        )}
      </Card>
    ) : null;

  const renderNotesCard = () => (
    <Card>
      <SectionLabel>Job Notes</SectionLabel>
      <div className="mt-2 flex items-end gap-2">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={2}
          placeholder="Add a field note…"
          data-testid="mjd-note-input"
          className="flex-1 resize-none px-3 py-2 rounded-lg border border-slate-200 text-[13px] bg-[#CCCDCE] text-[#262626] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4]"
        />
        <button
          onClick={addNote}
          disabled={savingNote || !noteText.trim()}
          data-testid="mjd-note-save-btn"
          className="h-10 px-3 rounded-lg bg-[#06B6D4] text-white text-[13px] font-bold disabled:opacity-50"
        >
          {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
        </button>
      </div>
      {notes.length > 0 && (
        <div className="mt-3 space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5">
              <StickyNote className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[13px] text-slate-700 leading-snug">{n.label}</div>
                <div className="text-[10.5px] text-slate-400 mt-0.5">{fmtLogTime(n.time)} · {n.byName || n.by}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {customer && (
        <div className="mt-4 pt-3 border-t border-slate-100 bg-[#1F3257]" data-testid="mjd-customer-notes">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-bold text-slate-400 mb-2">
            <StickyNote className="h-3 w-3 text-[#06B6D4]" /> Customer notes
          </div>
          {customerNotes.length > 0 ? (
            <div className="space-y-2">
              {customerNotes.map((n) => (
                <div
                  key={n.id}
                  data-testid={`mjd-customer-note-${n.id}`}
                  className="flex items-start gap-2 rounded-lg bg-[#CCCDCE] border border-cyan-100 p-2.5"
                >
                  <StickyNote className="h-3.5 w-3.5 text-[#06B6D4] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#262626] leading-snug">{n.text}</div>
                    <div className="text-[10.5px] text-[#434A5B] mt-0.5">
                      {(n.createdAt || "").slice(0, 10)}{(n.createdByName) ? ` · ${n.createdByName}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[12px] text-slate-400">No customer notes yet. Use the mic to add one by voice.</div>
          )}
        </div>
      )}
    </Card>
  );

  return (
    <MobileShell>
      {/* Voice assistant now mounted once globally in App.js, next to MobileNavHost */}

      {custLightbox && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
          onClick={(e) => { e.stopPropagation(); setCustLightbox(null); }}
          data-testid="mjd-customer-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setCustLightbox(null); }}
            data-testid="mjd-customer-lightbox-close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full">
            <img
              src={fileUrl(custLightbox.url)}
              alt={custLightbox.caption || "customer photo"}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            {custLightbox.caption && (
              <div className="mt-2 text-center text-white text-[13px]">{custLightbox.caption}</div>
            )}
          </div>
        </div>
      )}
      {/* App bar */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-2 h-14 bg-white border-b border-slate-200">
        <button
          onClick={goBack}
          data-testid="mjd-back-btn"
          className="flex items-center gap-1 h-10 px-2 rounded-lg text-[#60A5FA] font-bold text-[13px] hover:bg-slate-50"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back
        </button>
        <div className="text-[15px] font-black text-[#0B1E3F]">Job</div>
        <button
          onClick={sync}
          data-testid="mjd-sync-btn"
          aria-label="Sync"
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600"
        >
          <RefreshCw className={`h-5 w-5 ${syncing ? "animate-spin" : ""}`} strokeWidth={2.5} />
        </button>
      </header>

      {/* Customer / bill-to + Ask Guru */}
      <div className="px-4 pt-4">
        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Location / Bill to</div>
            <button
              onClick={() => navigate("/mobile/guru")}
              data-testid="mjd-ask-guru-btn"
              className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-2xl bg-gradient-to-br from-[#0B1E3F] to-[#2563EB] text-white text-[13px] font-bold shadow-sm active:scale-95 transition-transform"
            >
              Ask Guru <Bot className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-2 mt-2">
            <span className="text-[22px] font-black text-[#0B1E3F] leading-tight">{job.customer}</span>
            {membership && <MembershipBadge tier={membership} />}
          </div>

          <div className="mt-3 pt-3 border-t border-slate-700/40 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2.5 text-[14px] text-slate-200">
                <Clock className="h-[18px] w-[18px] text-blue-400 shrink-0" /> Scheduled
              </span>
              <span className="text-[14px] font-bold text-[#0B1E3F]">{fmtDateTime(job.start)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2.5 text-[14px] text-slate-200">
                <Briefcase className="h-[18px] w-[18px] text-blue-400 shrink-0" /> Job
              </span>
              <span className="text-[14px] font-bold text-[#0B1E3F] font-mono">{job.id}</span>
            </div>
            <button
              onClick={() => setShowContact((v) => !v)}
              data-testid="mjd-contact-toggle"
              className="w-full flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2.5 text-[14px] font-semibold text-blue-400">
                <Phone className="h-[18px] w-[18px] shrink-0" /> Contact details
              </span>
              <ChevronDown className={`h-5 w-5 text-blue-400 transition-transform ${showContact ? "rotate-180" : ""}`} />
            </button>
            {showContact && (
              <div className="space-y-2 pt-1" data-testid="mjd-contact-panel">
                {(job.phone || customer?.phone) && (
                  <a
                    href={`tel:${job.phone || customer?.phone}`}
                    className="flex items-center gap-2 text-[13px] text-slate-200 font-medium"
                  >
                    <Phone className="h-4 w-4 text-slate-400" /> {job.phone || customer?.phone}
                  </a>
                )}
                {customer?.email && (
                  <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-[13px] text-slate-200 font-medium">
                    <Mail className="h-4 w-4 text-slate-400" /> {customer.email}
                  </a>
                )}
                {customer && (
                  <div className="flex items-center gap-2 text-[12px] text-slate-400">
                    <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /> {customer.rating || 0} ·{" "}
                    {customer.jobsCount || 0} jobs · since {(customer.since || "").slice(0, 4) || "—"}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <nav className="sticky top-14 z-40 flex items-center w-full bg-white border-b border-slate-200 px-1 shadow-sm mt-4 overflow-x-auto no-scrollbar" data-testid="mjd-tabs">
        {TABS.map((t) => {
          const active = activeTab === t.key;
          const count =
            t.key === "estimates" ? estimates.length
            : t.key === "invoice" ? invoices.length
            : t.key === "tasks" ? openTaskCount
            : t.key === "notes" ? notes.length
            : t.key === "photos" ? customerPhotos.length
            : 0;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={t.testid}
              className={`relative shrink-0 whitespace-nowrap px-3.5 py-3 text-[13px] font-bold transition-colors min-h-[44px] ${
                active ? "text-[#06B6D4]" : "text-slate-500"
              }`}
            >
              {t.label}
              {count > 0 && (
                <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{count}</span>
              )}
              {active && <span className="absolute bottom-0 left-2 right-2 h-1 bg-[#06B6D4] rounded-full" />}
            </button>
          );
        })}
      </nav>

      <main className="flex-1 pb-12">
        {activeTab === "job" && (
          <div data-testid="mjd-job-tab" className="p-4 space-y-3">
            {/* Status */}
            <Card>
              <SectionLabel>Status</SectionLabel>
              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <span className={`inline-block px-4 py-1.5 rounded-full text-[12.5px] font-bold ${STATUS_SOLID[job.status] || "bg-slate-600 text-white"}`}>
                  {job.status}
                </span>
                <AssignTechControl
                  job={job}
                  techs={techs}
                  canAssign={canAssign}
                  onJobUpdated={(updated) => setJob(updated)}
                />
              </div>
              {job.status === "Completed" && payrollRecord && (
                <div className="mt-2.5 flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600" data-testid="mjd-payroll-recorded">
                  <DollarSign className="h-3.5 w-3.5" /> Pay recorded: {money0(payrollRecord.amount)} ({payrollRecord.payModel.replace("_", " ")})
                </div>
              )}
              <div className="grid grid-cols-2 gap-2.5 mt-4">
                {STATUS_ACTIONS.map((a) => {
                  const isCurrent = job.status === a.status;
                  const isMulti = (job.assignments || []).length > 1;
                  const isCompleteBtn = a.status === "Completed";
                  return (
                    <button
                      key={a.status}
                      onClick={() => requireLocation(() => (isCompleteBtn ? handleCompleteClick() : setStatus(a.status)))}
                      disabled={busyStatus || busyMyPart || isCurrent}
                      data-testid={`mjd-status-${a.status.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`flex flex-col items-center justify-center gap-1.5 h-[72px] rounded-xl border text-[12px] font-semibold transition-all disabled:opacity-100 ${
                        isCurrent
                          ? "bg-[#2563EB] border-[#60A5FA] text-white shadow-[0_0_14px_rgba(59,130,246,0.55)]"
                          : "bg-slate-50 border-slate-700/40 text-slate-200 active:bg-slate-100"
                      }`}
                    >
                      <a.icon className={`h-5 w-5 ${isCurrent ? "text-white" : "text-blue-400"}`} strokeWidth={2.5} />
                      {isCompleteBtn && isMulti ? "Complete my part" : a.label}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Teammates — visible whenever the job has 2+ techs. */}
            {(job.assignments || []).length > 1 && (
              <Card>
                <SectionLabel>Teammates on this job</SectionLabel>
                <div className="mt-2.5 flex flex-wrap gap-2" data-testid="mjd-teammates">
                  {job.assignments.map((a) => {
                    const isDone = a.status === "Done";
                    const dotColor = isDone
                      ? "#3b82f6"
                      : a.status === "In Progress"
                        ? "#10b981"
                        : a.status === "On Site"
                          ? "#6366f1"
                          : a.status === "En Route"
                            ? "#f59e0b"
                            : "#94a3b8";
                    return (
                      <span
                        key={a.techId}
                        data-testid={`mjd-teammate-${a.techId}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border ${
                          isDone
                            ? "bg-blue-50 text-blue-700 border-blue-200 line-through opacity-70"
                            : "bg-slate-50 text-slate-800 border-slate-200"
                        }`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: dotColor }} />
                        {a.techName}
                        <span className="text-[10.5px] font-normal opacity-70">· {a.status}</span>
                      </span>
                    );
                  })}
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  Everyone on the crew is equal — the last tech to finish closes the job.
                </div>
              </Card>
            )}

            {/* Required checklist (collapsible) */}
            <Card>
              <button
                onClick={() => setChecklistOpen((v) => !v)}
                data-testid="mjd-checklist-toggle"
                className="w-full flex items-center justify-between gap-2"
              >
                <SectionLabel>Required Checklist</SectionLabel>
                <div className="flex items-center gap-2">
                  {requiredFlows?.flows?.length > 0 && !requiredFlows.allComplete && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#2563EB] text-white">
                      {requiredFlows.flows.filter((f) => !f.completed).length}
                    </span>
                  )}
                  {requiredFlows?.allComplete && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  <ChevronDown className={`h-5 w-5 text-blue-400 transition-transform ${checklistOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {checklistOpen && (
                <div className="mt-3">
                  {requiredFlows && requiredFlows.flows && requiredFlows.flows.length > 0 ? (
                    <>
                      <div className="space-y-2.5" data-testid="mjd-required-flows">
                        {requiredFlows.flows.map((f) => (
                          <div key={f.id} className="flex items-center gap-2.5 text-[13px]">
                            {f.completed ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 fill-emerald-500/15" />
                            ) : (
                              <span className="h-5 w-5 rounded-full border-2 border-slate-500 shrink-0" />
                            )}
                            <span className={f.completed ? "text-slate-400 line-through" : "text-slate-100 font-medium"}>
                              {f.name}
                            </span>
                          </div>
                        ))}
                      </div>
                      {requiredFlows.allComplete ? (
                        requiredFlows.guruSummary ? (
                          <div className="rounded-xl bg-slate-50 p-3 mt-3" data-testid="mjd-guru-summary">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Job Summary</div>
                            <p className="text-[12.5px] text-slate-200 leading-relaxed">{requiredFlows.guruSummary}</p>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 mt-3">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Complete
                          </div>
                        )
                      ) : (
                        <button
                          onClick={() => setShowCompletion(true)}
                          data-testid="mjd-start-checklist-btn"
                          disabled={!hasModule("job_completion")}
                          className="w-full mt-4 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ClipboardCheck className="h-4 w-4" strokeWidth={2.5} />
                          {hasModule("job_completion") ? `Start required checklist (${requiredFlows.flows.filter((f) => !f.completed).length})` : "Job completion isn't in your plan"}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-[13px] text-slate-400">No required checklist for this job.</div>
                  )}
                </div>
              )}
            </Card>

            {/* Work-order record (parts + signature captured on completion) */}
            {(job.partsInstalled?.length > 0 || job.completionSignature?.image) && (
              <Card>
                <SectionLabel>Work Order Record</SectionLabel>
                {job.partsInstalled?.length > 0 && (
                  <div className="mt-2.5 space-y-1.5" data-testid="mjd-parts-installed">
                    {job.partsInstalled.map((p) => (
                      <div key={p.id || p.name} className="flex items-center justify-between text-[13px]">
                        <span className="text-slate-100 font-medium truncate">{p.name}</span>
                        <span className="text-slate-400 font-bold shrink-0">×{p.qty}</span>
                      </div>
                    ))}
                  </div>
                )}
                {job.completionSignature?.image && (
                  <div className="mt-3" data-testid="mjd-completion-signature">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Signed by {job.completionSignature.signedBy || "customer"}
                    </div>
                    <img
                      src={job.completionSignature.image}
                      alt="Customer signature"
                      className="h-16 bg-[#CCCDCE] rounded-lg border border-slate-200 p-1"
                    />
                  </div>
                )}
              </Card>
            )}

            {/* Address */}
            <Card>
              <SectionLabel>Address</SectionLabel>
              <div className="mt-2.5 flex items-start gap-2.5">
                <span className="h-9 w-9 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
                  <MapPin className="h-4 w-4 text-blue-400" />
                </span>
                <div className="text-[14px] font-semibold text-[#0B1E3F] leading-snug min-w-0">
                  {job.address}
                  <br />
                  {job.city}, {job.state} {job.zip}
                </div>
              </div>
              <a
                href={mapsHref}
                target="_blank"
                rel="noreferrer"
                data-testid="mjd-directions-btn"
                className="mt-3 w-full flex items-center justify-center gap-1.5 h-11 rounded-xl border border-blue-500/40 text-blue-400 text-[13px] font-bold active:bg-blue-500/10 transition-colors"
              >
                <NavIcon className="h-4 w-4" /> Directions
              </a>
            </Card>

            {/* Job Summary — separate from Work Description ("Problem summary"
                below); syncs with any linked estimate's Job Summary block. */}
            <Card>
              <div className="flex items-center justify-between">
                <SectionLabel>Job Summary</SectionLabel>
                {summaryDraft !== (job.guruSummary || "") && (
                  <button
                    onClick={saveSummary}
                    disabled={savingSummary}
                    data-testid="mjd-save-summary"
                    className="text-[12px] font-bold text-blue-600 active:opacity-60 disabled:opacity-50"
                  >
                    {savingSummary ? "Saving…" : "Save"}
                  </button>
                )}
              </div>
              <textarea
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                rows={4}
                placeholder="Summarize this job for the customer's estimate/invoice..."
                data-testid="mjd-summary-textarea"
                className="mt-2 w-full px-3 py-2 rounded-xl border-2 border-slate-800 text-[13.5px] leading-relaxed shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-y"
              />
            </Card>

            {/* Job details */}
            <Card>
              <SectionLabel>Job details</SectionLabel>
              <div className="mt-2 divide-y divide-slate-100">
                <InfoRow label="Type" value={job.type} />
                {job.trade && <InfoRow label="Business unit" value={job.trade} />}
                <InfoRow label="Date" value={fmtDate(job.start)} />
                <InfoRow label="Time" value={`${fmtTime(job.start)}${job.end ? ` – ${fmtTime(job.end)}` : ""}`} />
                {job.priority && <InfoRow label="Priority" value={job.priority} />}
              </div>
              {job.description && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <SectionLabel>Problem summary</SectionLabel>
                  <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
                </div>
              )}
            </Card>

            {/* Arrival window */}
            <Card>
              <SectionLabel>Arrival Window</SectionLabel>
              {job.arrivalStart || job.arrivalEnd ? (
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Starts</div>
                    <div className="text-[15px] font-black text-[#0B1E3F] mt-0.5">{fmtTime(job.arrivalStart)}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(job.arrivalStart || job.start)}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ends</div>
                    <div className="text-[15px] font-black text-[#0B1E3F] mt-0.5">{fmtTime(job.arrivalEnd)}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(job.arrivalEnd || job.start)}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-[13px] text-slate-400">No arrival window set. Add one when scheduling the job.</div>
              )}
            </Card>

            {/* Customer photos — saved to the customer file (visible everywhere) */}
            {renderCustomerPhotosCard()}

            {/* Notes */}
            {renderNotesCard()}
          </div>
        )}

        {activeTab === "history" && (
          <div data-testid="mjd-history-tab" className="p-4">
            <Card>
              {(job.log || []).length === 0 ? (
                <div className="text-[13px] text-slate-400 py-4 text-center">No activity yet.</div>
              ) : (
                <div className="relative pl-5">
                  <span className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-slate-200" />
                  {[...(job.log || [])].reverse().map((l) => (
                    <div key={l.id} className="relative pb-2.5 last:pb-0" data-testid={`mjd-log-${l.id}`}>
                      <span className="absolute -left-5 top-1 h-2.5 w-2.5 rounded-full bg-[#06B6D4] ring-2 ring-white" />
                      <div className="border border-[#262626] rounded-md overflow-hidden">
                        <div className="text-[13px] font-semibold text-[#0B1E3F] leading-snug bg-[#434A5B] px-2 py-1.5">{l.label}</div>
                        <div className="text-[11px] text-slate-400 px-2 py-1">
                          {fmtLogTime(l.time)}
                          {(l.byName || l.by) ? ` · ${l.byName || l.by}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === "notes" && (
          <div data-testid="mjd-notes-tab" className="p-4 space-y-3">
            {renderNotesCard()}
          </div>
        )}

        {activeTab === "tasks" && (
          <div data-testid="mjd-tasks-tab" className="p-4 space-y-3">
            <Card>
              <SectionLabel>Add task</SectionLabel>
              <div className="mt-2.5 space-y-2.5">
                <input
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addTask(); }}
                  placeholder="What needs to happen?"
                  data-testid="mjd-task-title-input"
                  className="w-full h-11 px-3 rounded-lg border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4]"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Calendar className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="datetime-local"
                      value={newTaskDue}
                      onChange={(e) => setNewTaskDue(e.target.value)}
                      data-testid="mjd-task-due-input"
                      className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4]"
                    />
                  </div>
                  <button
                    onClick={addTask}
                    disabled={taskBusy || !newTaskTitle.trim()}
                    data-testid="mjd-task-add-btn"
                    className="h-11 px-4 rounded-lg bg-[#06B6D4] text-white text-[14px] font-bold disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {taskBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={2.5} />} Add
                  </button>
                </div>
              </div>
            </Card>

            {tasks.length === 0 ? (
              <EmptyTab icon={ListChecks} title="No tasks yet" subtitle="Add a follow-up for this job above." />
            ) : (
              <div className="space-y-2" data-testid="mjd-tasks-list">
                {tasks.map((t) => (
                  <TaskRow key={t.id} task={t} onToggle={() => toggleTask(t)} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "photos" && (
          <div data-testid="mjd-photos-tab" className="p-4 space-y-3">
            {renderCustomerPhotosCard() || (
              <EmptyTab icon={ImageIcon} title="No customer linked" subtitle="Photos are saved to the customer file." />
            )}
          </div>
        )}

        {activeTab === "estimates" && (
          <div data-testid="mjd-estimates-tab" className="p-4 space-y-3">
            <button
              onClick={createEstimate}
              disabled={creatingEstimate}
              data-testid="mjd-create-estimate-btn"
              className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-[#06B6D4] text-white text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {creatingEstimate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={2.5} />} Create estimate
            </button>
            {estimates.length === 0 ? (
              <EmptyTab icon={FileText} title="No estimates yet" subtitle="Create one for this job above." />
            ) : (
              estimates.map((e) => <DocRow key={e.id} doc={e} kind="estimate" onOpen={() => openEstimateDoc(e.id)} />)
            )}
          </div>
        )}

        {activeTab === "invoice" && (
          <div data-testid="mjd-invoice-tab" className="p-4 space-y-3">
            {invoices.length === 0 ? (
              <EmptyTab icon={FileText} title="No invoices yet" subtitle="Invoices for this job will show here." />
            ) : (
              invoices.map((i) => <DocRow key={i.id} doc={i} kind="invoice" onOpen={() => openInvoiceDoc(i.id)} />)
            )}
          </div>
        )}
      </main>

      {openEstimate && (
        <DocDetail
          kind="estimate"
          doc={openEstimate}
          customers={customer ? [customer] : []}
          jobs={job ? [job] : []}
          mobileBackLabel="Job"
          mobileLayout
          onClose={() => {
            setOpenEstimate(null);
            load().catch(() => {});
          }}
          onUpdated={(updated) => {
            setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            setOpenEstimate(updated);
          }}
          onDeleted={(id) => {
            setEstimates((prev) => prev.filter((e) => e.id !== id));
            setOpenEstimate(null);
          }}
          onConverted={(newInvoice) => {
            // Close the loop: land the tech straight on the new invoice to collect payment.
            setOpenEstimate(null);
            setActiveTab("invoice");
            load().catch(() => {});
            if (newInvoice?.id) {
              setOpenInvoice(newInvoice);
              toast.success(`Invoice ${newInvoice.id} ready — collect payment`);
            } else {
              toast.success("Converted to invoice — see the Invoice tab");
            }
          }}
        />
      )}

      {openInvoice && (
        <DocDetail
          kind="invoice"
          doc={openInvoice}
          customers={customer ? [customer] : []}
          jobs={job ? [job] : []}
          mobileBackLabel="Job"
          mobileLayout
          onClose={() => {
            setOpenInvoice(null);
            load().catch(() => {});
          }}
          onUpdated={(updated) => {
            setInvoices((prev) => {
              const exists = prev.some((i) => i.id === updated.id);
              return exists ? prev.map((i) => (i.id === updated.id ? updated : i)) : [updated, ...prev];
            });
            setOpenInvoice(updated);
          }}
          onDeleted={(id) => {
            setInvoices((prev) => prev.filter((i) => i.id !== id));
            setOpenInvoice(null);
          }}
          onConverted={() => {}}
          onCloseJob={() => {
            setOpenInvoice(null);
            handleCompleteClick();
          }}
        />
      )}

      {showCompletion && (
        <WorkOrderComplete
          job={job}
          invoices={invoices}
          onClose={() => setShowCompletion(false)}
          onCompleted={completeJob}
        />
      )}

      {openJobAlert && (
        <OpenJobAlertModal
          jobId={openJobAlert.jobId}
          isAdmin={user?.role === "admin"}
          mobile
          onClose={() => setOpenJobAlert(null)}
          onOverride={() => {
            setOpenJobAlert(null);
            setStatus("En Route", { override: true });
          }}
        />
      )}
    </MobileShell>
  );
}

/* ---------------- sub-components ---------------- */
function MobileShell({ children }) {
  return (
    <div
      className="cc-mobile cc-glow relative w-full min-h-screen bg-slate-50 overflow-x-hidden flex flex-col"
      data-testid="mobile-job-detail-page"
    >
      <CommandThemeScope surface="mobile" />
      {children}
      {/* Dedicated height-based spacer (not padding) so bottom content clears the
          fixed mobile nav — a padding-bottom here would be silently overridden by
          `.cc-theme .p-4 { padding: ... !important }` in theme-command.css if this
          element (or an ancestor) ever picks up a p-3/4/5/6 class. */}
      <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(5rem + env(safe-area-inset-bottom))" }} />
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200">{children}</div>;
}

function SectionLabel({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-[12.5px] text-slate-500">{label}</span>
      <span className="text-[13.5px] font-bold text-[#0B1E3F] text-right">{value}</span>
    </div>
  );
}

function MembershipBadge({ tier }) {
  const s = MEMBERSHIP_STYLE[tier] || MEMBERSHIP_STYLE.Silver;
  return (
    <span
      data-testid="mjd-membership-badge"
      className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.text}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} /> {tier}
    </span>
  );
}

function DocRow({ doc, kind, onOpen }) {
  const solid =
    kind === "invoice"
      ? doc.status === "Paid"
        ? "bg-[#059669] text-white"
        : doc.status === "Voided"
          ? "bg-slate-700 text-slate-300"
          : "bg-[#D97706] text-white"
      : ["Accepted", "Converted"].includes(doc.status)
        ? "bg-[#059669] text-white"
        : doc.status === "Sent"
          ? "bg-[#2563EB] text-white"
          : "bg-slate-600 text-white";
  const Tag = onOpen ? "button" : "div";
  return (
    <Tag
      onClick={onOpen}
      className={`w-full text-left bg-white rounded-xl p-5 ${onOpen ? "active:scale-[0.99] transition-transform" : ""}`}
      data-testid={`mjd-doc-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[18px] font-black text-[#0B1E3F] leading-tight truncate">{doc.title || doc.id}</div>
          <div className="text-[12.5px] text-slate-400 font-mono mt-1">{doc.id}</div>
        </div>
        <span className={`shrink-0 px-3 py-1 rounded-full text-[12px] font-bold ${solid}`}>
          {doc.status}
        </span>
      </div>
      <div className="flex items-end justify-between mt-4 pt-4 border-t border-slate-700/40">
        <div>
          <div className="text-[12px] text-slate-400">Issue date</div>
          <div className="text-[15px] font-bold text-[#0B1E3F] mt-1">{fmtDate(doc.issuedAt)}</div>
        </div>
        <div className="text-right">
          <div className="text-[12px] text-slate-400">Total</div>
          <div className="text-[22px] font-black text-[#0B1E3F] mt-0.5">{money0(doc.total)}</div>
        </div>
      </div>
    </Tag>
  );
}

function EmptyTab({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon className="w-11 h-11 text-slate-300 mb-3" strokeWidth={1.75} />
      <div className="text-[15px] font-bold text-slate-700">{title}</div>
      <div className="text-[13px] text-slate-500 mt-1 max-w-[240px]">{subtitle}</div>
    </div>
  );
}

function TaskRow({ task, onToggle }) {
  const done = task.status === "done";
  const due = task.dueAt ? new Date(task.dueAt) : null;
  const overdue = !done && due && due.getTime() < Date.now();
  return (
    <div
      className="bg-white rounded-xl p-3.5 flex items-start gap-3 shadow-sm border border-slate-200"
      data-testid={`mjd-task-${task.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`mjd-task-toggle-${task.id}`}
        aria-label={done ? "Mark task open" : "Mark task done"}
        className="mt-0.5 shrink-0"
      >
        {done ? (
          <CheckCircle2 className="h-6 w-6 text-emerald-500 fill-emerald-500/15" />
        ) : (
          <Circle className="h-6 w-6 text-slate-300" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`text-[14px] font-semibold leading-snug ${done ? "text-slate-400 line-through" : "text-[#0B1E3F]"}`}>
          {task.title}
        </div>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
          {due && (
            <span className={`inline-flex items-center gap-1 text-[11.5px] font-medium ${overdue ? "text-rose-600" : "text-slate-400"}`}>
              <Calendar className="h-3.5 w-3.5" /> {fmtDateTime(task.dueAt)}{overdue ? " · overdue" : ""}
            </span>
          )}
          {task.assigneeName && (
            <span className="text-[11.5px] text-slate-400">· {task.assigneeName}</span>
          )}
        </div>
        {task.notes && <div className="text-[12.5px] text-slate-500 mt-1.5 leading-snug">{task.notes}</div>}
      </div>
    </div>
  );
}
